import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  FileText, Calendar, CheckCircle2, Wand2, Calculator, BarChart3, ChevronRight, Scale, 
  Search, Gavel, X, MessageSquare, Apple
} from 'lucide-react';

const MockWindow = ({ children, headerTitle, icon: Icon }: { children: React.ReactNode, headerTitle?: string, icon?: any }) => (
  <div className="w-full aspect-[4/3] bg-[#f8fafc] border border-slate-200/60 rounded-xl shadow-[0_12px_40px_-12px_rgba(0,0,0,0.1)] overflow-hidden relative flex flex-col scale-[1.02] origin-left">
    {/* Elegant App Header */}
    {headerTitle && (
      <div className="bg-white border-b border-slate-200/60 px-5 py-3 flex items-center justify-between z-10 shrink-0">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center">
              <Icon className="w-4 h-4 text-slate-600" />
            </div>
          )}
          <span className="text-sm font-bold text-slate-800">{headerTitle}</span>
        </div>
        <div className="flex gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-slate-200"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-slate-200"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-slate-200"></div>
        </div>
      </div>
    )}
    {!headerTitle && (
       <div className="bg-white border-b border-slate-200/60 px-4 py-2.5 flex items-center gap-2 z-10 shrink-0">
         <div className="flex gap-1.5">
           <div className="w-2.5 h-2.5 rounded-full bg-slate-200"></div>
           <div className="w-2.5 h-2.5 rounded-full bg-slate-200"></div>
           <div className="w-2.5 h-2.5 rounded-full bg-slate-200"></div>
         </div>
       </div>
    )}
    {/* Body */}
    <div className="flex-1 overflow-hidden relative isolate bg-slate-50">
      {children}
    </div>
  </div>
);

const Cursor = ({ animate }: { animate: any }) => (
  <motion.div 
    initial={{ x: '50%', y: '100%', opacity: 0 }}
    animate={animate}
    className="absolute z-50 pointer-events-none drop-shadow-md pb-[2px] pr-[2px]"
  >
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.5 3.21V20.8c0 .45.54.67.85.35l4.86-4.86c.15-.15.35-.23.56-.23h6.63c.45 0 .67-.54.35-.85L6.35 2.85c-.23-.23-.6-.23-.85 0z" fill="#0f172a" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  </motion.div>
);

// 1. Create an AI-Powered Case File
// Simulates Parse Draft / Upload Case Document
export const CaseFileAnimation = () => (
  <MockWindow headerTitle="Parse Document to Case">
    <div className="flex h-full w-full">
      {/* Sidebar Split */}
      <div className="w-1/3 bg-white border-r border-slate-200/60 p-4 pt-6 space-y-4 flex flex-col relative z-0">
         <div className="h-6 w-3/4 bg-slate-100 rounded"></div>
         <div className="flex flex-col gap-2 relative z-0">
           <div className="h-24 w-full bg-slate-50 border border-slate-200 border-dashed rounded-lg flex items-center justify-center">
              <div className="w-6 h-6 rounded bg-slate-200"></div>
           </div>
         </div>
         {/* Button animation */}
         <motion.div 
           animate={{ scale: [1, 0.98, 1, 1] }} 
           transition={{ duration: 4, repeat: Infinity, delay: 1 }}
           className="h-8 w-full bg-blue-600 rounded-lg shadow-sm flex items-center justify-center gap-2 mt-auto"
         >
           <Wand2 className="w-3.5 h-3.5 text-white" />
           <div className="w-12 h-2 bg-blue-300 rounded"></div>
         </motion.div>
      </div>

      {/* Main Content Area */}
      <div className="w-2/3 p-5 flex flex-col gap-4 relative isolate">
        {/* Extracted Details Populating */}
        <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-4 space-y-4">
           {/* Form rows */}
           <div className="flex gap-3">
             <div className="w-1/2 space-y-1">
               <div className="h-2 w-16 bg-slate-200 rounded"></div>
               <motion.div 
                 initial={{ opacity: 0, width: '0%' }}
                 animate={{ opacity: [0, 1, 1, 0], width: ['0%', '100%', '100%', '0%'] }}
                 transition={{ duration: 4, repeat: Infinity, delay: 1.5 }}
                 className="h-7 bg-blue-50/50 border border-blue-100 rounded flex items-center px-2 shadow-[inset_0_2px_4px_rgba(59,130,246,0.02)]"
               >
                 <div className="h-2 w-2/3 bg-blue-400 rounded"></div>
               </motion.div>
             </div>
             <div className="w-1/2 space-y-1">
               <div className="h-2 w-16 bg-slate-200 rounded"></div>
               <motion.div 
                 initial={{ opacity: 0, width: '0%' }}
                 animate={{ opacity: [0, 1, 1, 0], width: ['0%', '100%', '100%', '0%'] }}
                 transition={{ duration: 4, repeat: Infinity, delay: 1.8 }}
                 className="h-7 bg-blue-50/50 border border-blue-100 rounded flex items-center px-2"
               >
                 <div className="h-2 w-1/2 bg-blue-400 rounded"></div>
               </motion.div>
             </div>
           </div>

           <div className="space-y-1">
               <div className="h-2 w-16 bg-slate-200 rounded"></div>
               <motion.div 
                 initial={{ opacity: 0, width: '0%' }}
                 animate={{ opacity: [0, 1, 1, 0], width: ['0%', '100%', '100%', '0%'] }}
                 transition={{ duration: 4, repeat: Infinity, delay: 2.1 }}
                 className="h-7 bg-blue-50/50 border border-blue-100 rounded flex items-center px-2"
               >
                 <div className="h-2 w-3/4 bg-blue-400 rounded"></div>
               </motion.div>
           </div>
        </div>

        {/* Floating magic badge */}
        <motion.div 
          animate={{ opacity: [0, 0, 1, 0], y: [10, 0, 0, 10] }}
          transition={{ duration: 4, repeat: Infinity, delay: 1.5 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-slate-900 border border-slate-700 text-white shadow-xl px-3 py-1.5 rounded-full flex items-center gap-1.5 z-20"
        >
           <Wand2 className="w-3.5 h-3.5 text-blue-400" />
           <span className="text-[10px] font-medium tracking-wide">Fields Auto-Filled</span>
        </motion.div>
      </div>
    </div>
    
    <Cursor 
      animate={{ 
        x: ['10%', '15%', '15%', '10%'], 
        y: ['90%', '76%', '76%', '90%'],
        scale: [1, 0.9, 1, 1],
        opacity: [0, 1, 1, 0] 
      }} 
    />
  </MockWindow>
);

// 2. Track Court Proceedings
export const ProceedingsAnimation = () => (
  <MockWindow headerTitle="Case Detail - Proceedings">
    <div className="p-6 h-full space-y-4">
      {/* Timeline item 1 */}
      <div className="flex gap-4">
         <div className="flex flex-col items-center mt-1">
           <div className="w-6 h-6 bg-slate-100 border-2 border-white shadow-sm rounded-full flex items-center justify-center relative z-10 shrink-0">
             <div className="w-2.5 h-2.5 bg-slate-400 rounded-full"></div>
           </div>
           <div className="flex-1 w-0.5 bg-slate-200 mt-1 mb-1"></div>
         </div>
         <div className="flex-1 bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
           <div className="flex justify-between items-start mb-2">
             <div className="h-2.5 w-24 bg-slate-800 rounded"></div>
             <div className="h-2 w-16 bg-slate-300 rounded"></div>
           </div>
           <div className="h-1.5 w-3/4 bg-slate-200 rounded mb-1"></div>
           <div className="h-1.5 w-1/2 bg-slate-200 rounded"></div>
         </div>
      </div>

      {/* Timeline item 2 (New Proceeding Animation) */}
      <div className="flex gap-4 -mt-2">
         <div className="flex flex-col items-center mt-1">
           <div className="w-6 h-6 bg-blue-100 border-2 border-white shadow-sm rounded-full flex items-center justify-center relative z-10 shrink-0">
             <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
           </div>
           {/* Line extends down */}
         </div>
         <motion.div 
           animate={{ opacity: [0, 1, 1, 0], scale: [0.95, 1, 1, 0.95], y: [10, 0, 0, 10] }}
           transition={{ duration: 4, repeat: Infinity, ease: 'easeOut' }}
           className="flex-1 bg-white border border-blue-200 rounded-xl p-3 shadow-md ring-1 ring-blue-500/10 origin-top"
         >
           <div className="flex justify-between items-start mb-3">
             <div className="h-2.5 w-20 bg-slate-800 rounded"></div>
             {/* Next Hearing Badge */}
             <div className="px-2 py-0.5 bg-blue-50 border border-blue-100 rounded flex gap-1 items-center">
               <Calendar className="w-3 h-3 text-blue-600" />
               <div className="h-1.5 w-10 bg-blue-600/60 rounded"></div>
             </div>
           </div>
           {/* Typing effect */}
           <motion.div animate={{ width: ['0%', '80%', '80%', '0%'] }} transition={{ duration: 4, repeat: Infinity }} className="h-1.5 bg-slate-700/80 rounded mb-1.5 max-w-[80%]"></motion.div>
           <motion.div animate={{ width: ['0%', '40%', '40%', '0%'] }} transition={{ duration: 4, repeat: Infinity, delay: 0.2 }} className="h-1.5 bg-slate-700/80 rounded max-w-[50%]"></motion.div>
         </motion.div>
      </div>

    </div>
  </MockWindow>
);

// 3. Manage Diary
export const DiaryAnimation = () => (
  <MockWindow headerTitle="Court Diary">
    <div className="flex h-full w-full bg-white relative isolate">
       <div className="w-10 border-r border-slate-100 flex flex-col justify-between py-6 px-2">
         {[9, 10, 11].map(h => (
           <div key={h} className="text-[8px] text-slate-400 font-medium">{h}:00</div>
         ))}
       </div>
       <div className="flex-1 flex flex-col divide-y divide-slate-100">
         {/* Today Section Header */}
         <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
           <div className="flex items-center gap-2">
             <span className="text-[10px] font-bold text-slate-700 uppercase tracking-widest">Today</span>
             <div className="w-4 h-4 rounded text-[8px] bg-blue-100 text-blue-700 flex items-center justify-center font-bold">2</div>
           </div>
         </div>
         
         <div className="p-4 relative flex-1">
           {/* Event 1 */}
           <motion.div 
             animate={{ opacity: [1, 1, 1, 1] }} // static
             className="w-full bg-white border border-slate-200 shadow-sm rounded-lg p-3 hover:bg-slate-50 relative mb-3 hover:border-blue-300"
           >
             <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-400 rounded-l-lg"></div>
             <div className="flex justify-between items-center mb-1 pl-1">
               <div className="h-2 w-24 bg-slate-800 rounded"></div>
               <div className="h-2 w-8 bg-slate-300 rounded"></div>
             </div>
             <div className="h-1.5 w-16 bg-slate-400 rounded pl-1"></div>
           </motion.div>

           {/* Event 2 - Animating in */}
           <motion.div 
             initial={{ opacity: 0 }}
             animate={{ opacity: [0, 1, 1, 0], scale: [0.95, 1, 1, 0.95], y: [10, 0, 0, 10] }}
             transition={{ duration: 4, repeat: Infinity, ease: 'easeOut', delay: 0.5 }}
             className="w-full bg-white border border-blue-200 shadow-md ring-1 ring-blue-500/5 rounded-lg p-3 relative"
           >
             <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-l-lg"></div>
             <div className="flex justify-between items-center mb-1 pl-1">
               <div className="h-2 w-32 bg-slate-800 rounded"></div>
               <div className="h-2 w-10 bg-blue-600/60 rounded"></div>
             </div>
             <div className="h-1.5 w-20 bg-slate-400 rounded pl-1"></div>
           </motion.div>
         </div>
       </div>
    </div>
  </MockWindow>
);

// 4. Intelligent LexDraft
export const DraftAnimation = () => (
  <MockWindow headerTitle="LexDraft">
    <div className="h-full flex flex-col bg-slate-100/50">
      {/* Fake Editor Toolbar */}
      <div className="h-10 bg-white border-b border-slate-200/60 flex items-center px-4 gap-4 shrink-0">
         <div className="w-16 h-2 rounded bg-slate-200"></div>
         <div className="flex gap-2">
           <div className="w-4 h-4 rounded bg-slate-200/60"></div>
           <div className="w-4 h-4 rounded bg-slate-200/60"></div>
           <div className="w-4 h-4 rounded bg-slate-200/60"></div>
         </div>
      </div>

      <div className="flex-1 flex justify-center p-4 relative overflow-hidden">
        {/* Editor Document */}
        <div className="w-[85%] bg-white border border-slate-200 shadow-md h-full rounded shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 pt-8 space-y-4 relative">
          <div className="h-1.5 w-20 bg-slate-300 mx-auto rounded"></div>
          <div className="h-2 w-1/2 bg-slate-800 mx-auto rounded mt-2 mb-6"></div>

          <div className="space-y-2.5">
            <div className="h-1.5 w-full bg-slate-200 rounded"></div>
            <div className="h-1.5 w-[90%] bg-slate-200 rounded"></div>
            <motion.div animate={{ width: ['0%', '75%', '75%', '0%'] }} transition={{ duration: 4, repeat: Infinity, delay: 1 }} className="h-1.5 bg-blue-600/30 rounded"></motion.div>
          </div>
        </div>

        {/* AI Selection Popover */}
        <motion.div 
          animate={{ opacity: [0, 0, 1, 0], scale: [0.95, 0.95, 1, 0.95], y: [10, 10, 0, 10] }}
          transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 w-48 bg-[#0a0a0a] rounded-xl shadow-2xl border border-white/10 p-1.5 flex items-center gap-1.5 z-20"
        >
          <div className="pl-2">
             <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5 text-white/90">
               <path d="M12 2C12 2 12 7 7 7C12 7 12 12 12 12C12 12 12 7 17 7C12 7 12 2 12 2Z" fill="currentColor"/>
               <path d="M19 13C19 13 19 15.5 16.5 15.5C19 15.5 19 18 19 18C19 18 19 15.5 21.5 15.5C19 15.5 19 13 19 13Z" fill="currentColor"/>
             </svg>
          </div>
          <div className="bg-white/10 px-3 py-1.5 rounded-lg flex-1 cursor-pointer hover:bg-white/20 transition-colors">
            <span className="text-[10px] text-white font-medium">Improve phrasing</span>
          </div>
        </motion.div>
      </div>

    </div>
  </MockWindow>
);

// 5. Record Client Fee
export const FeeAnimation = () => (
  <MockWindow headerTitle="Client Fee">
    <div className="p-5 h-full flex flex-col gap-5 bg-white">
      {/* Total Card */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex justify-between items-center">
        <div>
           <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1.5">Total Fee Agreed</div>
           <div className="flex items-center gap-1">
             <span className="text-slate-400 font-bold text-lg">Rs.</span>
             <span className="text-xl font-bold text-slate-800 tracking-tight">1,50,000</span>
           </div>
        </div>
        <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 shadow-sm flex items-center justify-center">
          <Calculator className="w-4 h-4 text-slate-400" />
        </div>
      </div>
      
      {/* Payments List */}
      <div className="flex-1 border border-slate-100 rounded-xl overflow-hidden flex flex-col">
         <div className="bg-slate-50 px-3 py-2 border-b border-slate-100 flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">
           <span>Payments</span>
           <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Paid 50%</span>
         </div>
         <div className="p-3 space-y-3 relative">
           <div className="flex justify-between items-center border-b border-slate-50 pb-2">
             <div className="h-2 w-20 bg-slate-800 rounded"></div>
             <div className="h-2 w-12 bg-slate-300 rounded"></div>
           </div>
           
           {/* Animated New Payment */}
           <motion.div 
             animate={{ opacity: [0, 1, 1, 0], x: [-10, 0, 0, -10] }}
             transition={{ duration: 4, repeat: Infinity, delay: 1 }}
             className="flex justify-between items-center bg-blue-50/50 p-2 rounded -mx-2 ring-1 ring-blue-500/10"
           >
             <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" /></div>
                <div className="h-2 w-16 bg-slate-800 rounded"></div>
             </div>
             <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-600">
               + <div className="h-2 w-10 bg-emerald-600/80 rounded"></div>
             </div>
           </motion.div>
         </div>
      </div>
    </div>
  </MockWindow>
);

// 6. View Analytics
export const AnalyticsAnimation = () => (
  <MockWindow headerTitle="Case Metrics">
    <div className="h-full p-4 bg-white flex flex-col gap-3">
      {/* Top Value Cards (Matches Case Metrics section) */}
      <div className="flex gap-2 h-[35%]">
         <div className="flex-1 bg-white border border-slate-200 rounded-lg p-3 flex flex-col justify-center relative shadow-sm">
            <div className="text-[7.5px] font-bold text-slate-500 uppercase tracking-widest mb-1">Total Cases</div>
            <motion.div animate={{ opacity:[0,1,1,1] }} transition={{duration:3, repeat:Infinity}} className="text-xl font-semibold text-slate-800">124</motion.div>
         </div>
         <div className="flex-1 bg-white border border-slate-200 rounded-lg p-3 flex flex-col justify-center relative shadow-sm">
            <div className="text-[7.5px] font-bold text-slate-500 uppercase tracking-widest mb-1">Active Cases</div>
            <div className="text-xl font-semibold text-blue-500">86</div>
         </div>
         <div className="flex-1 bg-white border border-slate-200 rounded-lg p-3 flex flex-col justify-center relative shadow-sm">
            <div className="text-[7.5px] font-bold text-slate-500 uppercase tracking-widest mb-1">New This Month</div>
            <div className="text-xl font-semibold text-slate-800">12</div>
         </div>
      </div>
      
      {/* Graph Area (Matches Cases by Matter Type) */}
      <div className="flex-1 border border-slate-100 rounded-lg p-3 flex flex-col gap-2 relative">
        <div className="text-[9px] font-semibold text-slate-800 tracking-wide mb-1">Top Matter Types</div>
        
        {/* Horizontal bars */}
        <div className="flex-1 flex flex-col gap-2 justify-center">
          {[
            { label: 'Civil', width: 85, color: 'bg-blue-600' },
            { label: 'Criminal', width: 60, color: 'bg-slate-300' },
            { label: 'Family', width: 45, color: 'bg-slate-300' },
            { label: 'Corporate', width: 30, color: 'bg-slate-300' }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="w-12 text-[8px] text-slate-500 truncate text-right">{item.label}</div>
              <div className="flex-1 h-3.5 bg-slate-50 rounded-r-sm overflow-hidden">
                <motion.div 
                  initial={{ width: '0%' }}
                  animate={{ width: [`0%`, `${item.width}%`, `${item.width}%`, '0%'] }}
                  transition={{ duration: 4, repeat: Infinity, delay: i * 0.1, ease: "easeOut" }}
                  className={`h-full ${item.color} rounded-r-sm`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Floating Tooltip */}
        <motion.div 
          animate={{ opacity: [0, 0, 1, 0], y: [10, 0, 0, 10], x: [0, 20, 20, 0] }}
          transition={{ duration: 4, repeat: Infinity, delay: 1.5, ease: "easeOut" }}
          className="absolute top-6 left-1/3 bg-white text-slate-800 rounded shadow-lg p-1.5 border border-slate-100 z-20 flex flex-col gap-0.5 w-16"
        >
          <div className="text-[7px] text-slate-400 font-bold uppercase tracking-widest leading-tight">Civil</div>
          <div className="flex justify-between items-center text-[9px]">
             <span>Cases</span>
             <span className="font-bold">42</span>
          </div>
        </motion.div>
      </div>
    </div>
  </MockWindow>
);

